import {useContext, useMemo, useState, useRef} from "react";
import {
    Box, Button, Grid, InputAdornment,
    IconButton, MenuItem, Paper, Radio, Select, TextField, Tooltip, Typography, Snackbar, Alert
} from "@mui/material";
import {InfoOutlined, Email, Sms, Notifications, AutoFixHigh} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import SectionHeader from "../../../../util/component/SectionHeader";
import {createTemplate} from "../../api/templateApi";
import {TemplateType, resolveTenantIdFromName, isMultiTenant, VALID_COUNTRY_SUFFIXES, extractApiError, getErrorMessage} from "../../types";
import {getChannelNameConflicts, CHANNEL_FORBIDDEN_TOKENS} from "../../utils/channelNameValidation";
import WysiwygEditor from "../../components/editor/WysiwygEditor";
import ResultDialog from "../../components/ResultDialog";
import CancelConfirmDialog from "../../components/CancelConfirmDialog";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useHtmlFormatter} from "../../hooks/useHtmlFormatter";

const EMAIL_SKELETON = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
</head>
<body>

</body>
</html>`;

type EditorMode = "html" | "design";
type PreviewMode = "preview" | "nopreview";

interface CreateTemplateForm {
    name: string;
    type: TemplateType;
    subject: string;
    body: string;
}

const CreateTemplate = () => {
    const navigate = useNavigate();
    const {t} = useTranslation(["translation", "templates"]);
    const {globalData} = useContext(GlobalContext);
    const user = globalData?.user;
    const username: string = user?.username || 'unknown';
    const multiTenant = isMultiTenant(user);
    
    const { formatHtml, snackbarOpen, snackbarMessage, snackbarSeverity, closeSnackbar } = useHtmlFormatter();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const lineNumbersRef = useRef<HTMLDivElement>(null);

    const [saving, setSaving] = useState(false);
    const [editorMode, setEditorMode] = useState<EditorMode>("html");
    const [previewMode, setPreviewMode] = useState<PreviewMode>("preview");
    const [plainText, setPlainText] = useState("");
    const [plainTextError, setPlainTextError] = useState(false);
    const [emailBodyError, setEmailBodyError] = useState(false);
    const [bodyTouched, setBodyTouched] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [resultSuccess, setResultSuccess] = useState(false);
    const [resultErrorMessage, setResultErrorMessage] = useState<string | undefined>(undefined);
    const [createdName, setCreatedName] = useState("");
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

    const validationSchema = Yup.object({
        name: Yup.string()
            .required("*" + t('templates:createForm.requiredCheck'))
            .matches(/^[a-zA-Z0-9_]+$/, "*" + t('templates:createForm.invalidName'))
            .max(128, "*" + t('templates:createForm.maxLength', {max: 128}))
            .test('country-suffix', "*" + t('templates:createForm.invalidSuffix', {suffixes: VALID_COUNTRY_SUFFIXES.map(s => '_' + s).join(', ')}),
                (value) => {
                    if (!multiTenant) return true; // single-tenant: nessuna validazione suffisso
                    if (!value || value.length < 3) return false;
                    const suffix = value.slice(-2).toLowerCase();
                    return VALID_COUNTRY_SUFFIXES.includes(suffix) && value[value.length - 3] === '_';
                }
            )
            .test('channel-name-conflict', '',
                function (value) {
                    if (!value) return true;
                    const { type: selectedType } = this.parent as CreateTemplateForm;
                    if (!selectedType) return true;
                    const conflicts = getChannelNameConflicts(value, selectedType as TemplateType);
                    if (conflicts.length > 0) {
                        return this.createError({
                            message: "*" + t('templates:createForm.channelNameConflict', {
                                channel: selectedType,
                                forbidden: (CHANNEL_FORBIDDEN_TOKENS[selectedType] || []).join(', '),
                            }),
                        });
                    }
                    return true;
                }
            ),
        type: Yup.string().required("*" + t('templates:createForm.requiredCheck')),
        subject: Yup.string().when("type", {
            is: (val: string) => val === "EMAIL" || val === "PUSH",
            then: Yup.string().required("*" + t('templates:createForm.requiredCheck')),
            otherwise: Yup.string().nullable(),
        }),
        body: Yup.string().required("*" + t('templates:createForm.requiredCheck')),
    });

    const methods = useForm<CreateTemplateForm>({
        defaultValues: {name: "", type: "EMAIL", subject: "", body: EMAIL_SKELETON},
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
    });

    const {handleSubmit, control, watch, setValue, trigger, formState} = methods;
    const type = watch("type");
    const body = watch("body");
    const subject = watch("subject");
    const name = watch("name");

    const stripHtml = (html: string): string => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        return doc.body.textContent?.trim() || "";
    };

    const extractBodyContent = (html: string): string => {
        const match = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        return match ? match[1].trim() : html.trim();
    };

    const isEmailBodyEmpty = (html: string): boolean => {
        if (!html) return true;
        const lower = html.toLowerCase();
        const openStart = lower.indexOf('<body');
        const closeStart = lower.indexOf('</body>');
        if (openStart === -1 || closeStart === -1 || closeStart < openStart) return true;
        const openEnd = html.indexOf('>', openStart);
        if (openEnd === -1 || openEnd >= closeStart) return true;
        const inner = html.substring(openEnd + 1, closeStart);
        const container = document.createElement('div');
        container.innerHTML = inner;
        const hasText = !!container.textContent?.trim();
        const hasVisibleElement = !!container.querySelector('img, hr, input, video, audio, iframe, svg, table');
        return !hasText && !hasVisibleElement;
    };

    const isNameMissing = !name?.trim();
    const isSubjectMissing = (type === "EMAIL" || type === "PUSH") && !subject?.trim();
    const isBodyMissing = type === "EMAIL"
        ? isEmailBodyEmpty(body || "")
        : !plainText.trim();
    const hasFormErrors = !formState.isValid;
    const isCreateDisabled = saving || isNameMissing || isSubjectMissing || isBodyMissing || hasFormErrors;

    // Errore del corpo email in tempo reale: vuoto dopo che l'utente ha interagito, oppure dopo un tentativo di submit
    const showEmailBodyError = type === "EMAIL" && (emailBodyError || (bodyTouched && isEmailBodyEmpty(body || "")));

    const handleFormatHtml = () => {
        const formatted = formatHtml(body);
        if (formatted) {
            setValue("body", formatted, { shouldValidate: true });
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
        if (lineNumbersRef.current && e.currentTarget) {
            lineNumbersRef.current.scrollTop = e.currentTarget.scrollTop;
        }
    };

    const handleTypeChange = (newType: TemplateType) => {
        const prevType = type;
        setValue("type", newType);
        // Ri-valida il nome perché la regola channel-name-conflict dipende dal tipo
        trigger("name");
        if (prevType === "EMAIL" && newType !== "EMAIL") {
            setPlainText(stripHtml(body));
        } else if (prevType !== "EMAIL" && newType === "EMAIL") {
            const text = plainText.trim();
            if (text) {
                setValue("body", `<!DOCTYPE html>\n<html lang="en">\n<head>\n    <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />\n</head>\n<body>\n${text}\n</body>\n</html>`);
            } else if (!body.trim() || stripHtml(body) === "") {
                setValue("body", EMAIL_SKELETON);
            }
        }
    };

    const onSubmit = async (data: CreateTemplateForm) => {
        if (data.type !== "EMAIL" && !plainText.trim()) {
            setPlainTextError(true);
            return;
        }
        if (data.type === "EMAIL" && isEmailBodyEmpty(data.body)) {
            setEmailBodyError(true);
            return;
        }
        setPlainTextError(false);
        setEmailBodyError(false);
        setSaving(true);
        try {
            const finalBody = data.type === "EMAIL" ? extractBodyContent(data.body) : plainText;
            const tenantId = multiTenant
                ? resolveTenantIdFromName(data.name.trim())
                : user?.legalEntities?.[0]?.name || resolveTenantIdFromName(data.name.trim());

            const result = await createTemplate({
                name: data.name.trim(),
                type: data.type,
                tenantId,
                createdBy: username,
                versions: [{
                    subjectTitle: (data.type === "EMAIL" || data.type === "PUSH") ? data.subject.trim() : undefined,
                    body: finalBody,
                }],
            });
            if (result?.name) {
                setCreatedName(result.name);
                setResultSuccess(true);
                setResultOpen(true);
            }
        } catch (e: any) {
            setResultSuccess(false);
            
            const apiError = extractApiError(e);
            if (apiError) {
                const errorMsg = getErrorMessage(apiError.code, t);
                setResultErrorMessage(errorMsg);
            } else {
                setResultErrorMessage(t('templates:errors.genericError'));
            }
            
            setResultOpen(true);
        } finally {
            setSaving(false);
        }
    };

    const lineNumbers = useMemo(() => {
        const count = body.split("\n").length;
        return Array.from({length: count}, (_, i) => i + 1);
    }, [body]);

    const renderTemplateNameField = () => (
        <Controller control={control} name="name" render={({field, fieldState}) => {
            const nameError = fieldState.error;
            const shouldShowError = !!nameError;
            // Traduci il messaggio al momento del render in base al tipo di errore,
            // così segue la lingua corrente anche se cambia dopo la validazione.
            const errorMessageByType: Record<string, string> = {
                required: "*" + t('templates:createForm.requiredCheck'),
                matches: "*" + t('templates:createForm.invalidName'),
                max: "*" + t('templates:createForm.maxLength', {max: 128}),
                'country-suffix': "*" + t('templates:createForm.invalidSuffix', {suffixes: VALID_COUNTRY_SUFFIXES.map(s => '_' + s).join(', ')}),
                'channel-name-conflict': "*" + t('templates:createForm.channelNameConflict', {
                    channel: type,
                    forbidden: (CHANNEL_FORBIDDEN_TOKENS[type] || []).join(', '),
                }),
            };
            const errorMessage = nameError
                ? (errorMessageByType[nameError.type ?? ''] ?? nameError.message)
                : undefined;
            return (
                <TextField
                    fullWidth size="small" required
                    label={t('templates:createForm.templateNameLabel')}
                    helperText={shouldShowError ? errorMessage : undefined}
                    error={shouldShowError}
                    value={field.value}
                    onChange={field.onChange}
                    inputProps={{maxLength: 128}}
                    InputProps={multiTenant ? {
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={t('templates:createForm.templateNameHelperMultiTenant', {suffixes: VALID_COUNTRY_SUFFIXES.map(s => '_' + s).join(', ')})} arrow placement="top">
                                    <InfoOutlined sx={{fontSize: 20, color: 'primary.main', cursor: 'help'}} />
                                </Tooltip>
                            </InputAdornment>
                        )
                    } : undefined}
                />
            );
        }}/>
    );

    const renderEmailDetails = () => (
        <Paper variant="outlined" sx={{mt: 2}}>
            <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="subtitle1" fontWeight={700}>{t('templates:createForm.emailDetails')}</Typography>
            </Box>
            <Box sx={{px: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider'}}>
                <Controller
                    control={control}
                    name="subject"
                    render={({field, fieldState}) => (
                        <TextField
                            fullWidth size="small" required
                            label={t('templates:createForm.subjectLabel')}
                            placeholder={t('templates:createForm.subjectPlaceholder')}
                            value={field.value}
                            onChange={field.onChange}
                            error={!!fieldState.error}
                            helperText={fieldState.error ? "*" + t('templates:createForm.requiredCheck') : undefined}
                        />
                    )}
                />
            </Box>
            <Box sx={{px: 2, pt: 2, pb: 1}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                    <Typography variant="subtitle2" fontWeight={700}>{t('templates:createForm.bodyLabel')}</Typography>
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                        {editorMode === "html" && (
                            <Tooltip title={t('templates:createForm.formatHtmlTooltip')} arrow>
                                <IconButton size="small" onClick={handleFormatHtml} sx={{color: 'primary.main'}}>
                                    <AutoFixHigh fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        )}
                        <Select size="small" value={editorMode} onChange={(e) => setEditorMode(e.target.value as EditorMode)} sx={{minWidth: 100}}>
                            <MenuItem value="html">HTML</MenuItem>
                            <MenuItem value="design">Design</MenuItem>
                        </Select>
                        {editorMode === "html" && (
                            <Select size="small" value={previewMode} onChange={(e) => setPreviewMode(e.target.value as PreviewMode)} sx={{minWidth: 120}}>
                                <MenuItem value="preview">{t('templates:detailContent.preview')}</MenuItem>
                                <MenuItem value="nopreview">{t('templates:detailContent.noPreview')}</MenuItem>
                            </Select>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box sx={{display: 'flex', border: '1px solid', borderColor: showEmailBodyError ? 'error.main' : 'divider', mx: 2, mb: showEmailBodyError ? 0 : 2, minHeight: 400, maxHeight: 700, transition: 'min-height 0.2s'}}>
                {editorMode === "html" ? (
                    <Box sx={{display: 'flex', flex: 1}}>
                        <Box ref={lineNumbersRef} sx={{bgcolor: 'grey.100', borderRight: '1px solid', borderColor: 'divider', px: 1, py: 1, fontFamily: 'monospace', fontSize: 13, lineHeight: '20px', color: 'text.secondary', userSelect: 'none', textAlign: 'right', minWidth: 40, overflow: 'hidden'}}>
                            {lineNumbers.map(n => <div key={n}>{n}</div>)}
                        </Box>
                        <Controller control={control} name="body" render={({field}) => (
                            <Box component="textarea" ref={textareaRef} value={field.value} onChange={(e: any) => { field.onChange(e.target.value); setBodyTouched(true); setEmailBodyError(false); }} onScroll={handleScroll} spellCheck={false}
                                sx={{flex: 1, border: 'none', outline: 'none', resize: 'none', fontFamily: 'monospace', fontSize: 13, lineHeight: '20px', p: 1, overflow: 'auto', bgcolor: 'background.paper'}}
                            />
                        )}/>
                    </Box>
                ) : null}
                {/* Keep the WYSIWYG editor mounted across mode switches so its
                    undo/redo history is not lost when toggling to HTML and back. */}
                <Box sx={{display: editorMode === "design" ? 'flex' : 'none', flex: 1, flexDirection: 'column', minHeight: 400}}>
                    <WysiwygEditor
                        engine="custom"
                        initialHtml={extractBodyContent(body)}
                        onChange={(html) => {
                            const fullHtml = `<!DOCTYPE html>\n<html lang="en">\n<head>\n    <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />\n</head>\n<body>\n${html}\n</body>\n</html>`;
                            setValue("body", fullHtml);
                            setBodyTouched(true);
                            setEmailBodyError(false);
                        }}
                    />
                </Box>
                {editorMode === "html" && previewMode === "preview" && (
                    <Box sx={{flex: 1, borderLeft: '1px solid', borderColor: 'divider', overflow: 'hidden', bgcolor: '#fff'}}>
                        <iframe title="preview" srcDoc={body} style={{width: '100%', height: '100%', border: 'none', minHeight: 400}} sandbox="allow-same-origin"/>
                    </Box>
                )}
            </Box>
            {showEmailBodyError && (
                <Typography variant="caption" color="error" sx={{mx: 2, mt: 0.5, mb: 2, display: 'block'}}>
                    {"*" + t('templates:createForm.requiredCheck')}
                </Typography>
            )}
        </Paper>
    );

    const renderSmsContent = () => (
        <Paper variant="outlined" sx={{mt: 2}}>
            <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                <Typography variant="subtitle1" fontWeight={700}>{t('templates:createForm.smsDetails')}</Typography>
            </Box>
            <Box sx={{p: 2}}>
                <TextField fullWidth multiline required label={t('templates:createForm.bodyLabel')} minRows={6} maxRows={15}
                    placeholder={t('templates:createForm.smsPlaceholder')}
                    value={plainText}
                    onChange={(e) => { setPlainText(e.target.value); setPlainTextError(false); }}
                    error={plainTextError && !plainText.trim()}
                    helperText={plainTextError && !plainText.trim() ? "*" + t('templates:createForm.requiredCheck') : undefined}
                />
            </Box>
        </Paper>
    );

    const renderPushContent = () => (
        <Paper variant="outlined" sx={{mt: 2}}>
            <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                <Typography variant="subtitle1" fontWeight={700}>{t('templates:createForm.pushDetails')}</Typography>
            </Box>
            <Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                <Controller control={control} name="subject" render={({field, fieldState}) => (
                    <TextField fullWidth required size="small" label={t('templates:createForm.titleLabel')}
                        placeholder={t('templates:createForm.titlePlaceholder')}
                        value={field.value} onChange={field.onChange}
                        error={!!fieldState.error} helperText={fieldState.error ? "*" + t('templates:createForm.requiredCheck') : undefined}
                    />
                )}/>
                <TextField fullWidth multiline required label={t('templates:createForm.bodyLabel')} minRows={4} maxRows={10}
                    placeholder={t('templates:createForm.bodyPlaceholder')}
                    value={plainText}
                    onChange={(e) => { setPlainText(e.target.value); setPlainTextError(false); }}
                    error={plainTextError && !plainText.trim()}
                    helperText={plainTextError && !plainText.trim() ? "*" + t('templates:createForm.requiredCheck') : undefined}
                />
            </Box>
        </Paper>
    );

    return (
        <>
            <SectionHeader title={t('templates:createHeader.title')} subtitle={t('templates:createHeader.subTitle')}/>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Paper elevation={1} sx={{margin: '18px', padding: '24px'}}>
                        <Grid container spacing={3}>
                            {/* Channel type */}
                            <Grid item xs={12}>
                                <Paper variant="outlined">
                                    <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                                        <Typography variant="subtitle1" fontWeight={700}>{t('templates:createForm.channelLabel')}</Typography>
                                    </Box>
                                    <Box sx={{px: 2, py: 2}}>
                                        <Grid container spacing={2}>
                                    {[
                                        { value: 'EMAIL', icon: <Email sx={{fontSize: 24}}/>, label: 'Email', description: t('templates:createForm.emailDescription') },
                                        { value: 'SMS', icon: <Sms sx={{fontSize: 24}}/>, label: 'SMS', description: t('templates:createForm.smsDescription') },
                                        { value: 'PUSH', icon: <Notifications sx={{fontSize: 24}}/>, label: 'Push', description: t('templates:createForm.pushDescription') }
                                    ].map((channel) => (
                                        <Grid item xs={12} md={4} key={channel.value}>
                                            <Paper
                                                elevation={type === channel.value ? 3 : 0}
                                                variant={type === channel.value ? 'elevation' : 'outlined'}
                                                onClick={() => handleTypeChange(channel.value as TemplateType)}
                                                sx={{
                                                    p: 2.5,
                                                    height: '90px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    cursor: 'pointer',
                                                    border: type === channel.value ? '2px solid' : '1px solid',
                                                    borderColor: type === channel.value ? 'accent.main' : 'divider',
                                                    bgcolor: type === channel.value ? 'accent.lighter' : 'background.paper',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        borderColor: 'accent.main',
                                                        bgcolor: type === channel.value ? 'accent.lighter' : 'action.hover'
                                                    }
                                                }}
                                            >
                                                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
                                                    <Radio
                                                        checked={type === channel.value}
                                                        value={channel.value}
                                                        sx={{p: 0, color: 'accent.main', '&.Mui-checked': {color: 'accent.main'}}}
                                                    />
                                                    <Box sx={{flex: 1}}>
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                                                            <Box sx={{color: type === channel.value ? 'accent.main' : 'text.secondary'}}>
                                                                {channel.icon}
                                                            </Box>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {channel.label}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{
                                                            fontSize: '0.875rem',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            lineHeight: 1.4
                                                        }}>
                                                            {channel.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Template Name section */}
                            <Grid item xs={12}>
                                <Paper variant="outlined">
                                    <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                                        <Typography variant="subtitle1" fontWeight={700}>{t('templates:createForm.templateNameLabel')}</Typography>
                                    </Box>
                                    <Box sx={{px: 2, py: 2}}>
                                        {renderTemplateNameField()}
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Content sections */}
                            <Grid item xs={12}>
                                {type === "EMAIL" && renderEmailDetails()}
                                {type === "SMS" && renderSmsContent()}
                                {type === "PUSH" && renderPushContent()}
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Action buttons */}
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2, mx: '18px', mb: 3}}>
                        <Button variant="outlined" onClick={() => setCancelConfirmOpen(true)}>
                            {t('templates:actions.cancel')}
                        </Button>
                        <Button type="submit" variant="contained" disabled={isCreateDisabled}>
                            {saving ? t('templates:actions.saving') : t('templates:actions.create')}
                        </Button>
                    </Box>
                </form>
            </FormProvider>

            <CancelConfirmDialog 
                open={cancelConfirmOpen} 
                onClose={() => setCancelConfirmOpen(false)} 
                onConfirm={() => window.close()}
            />

            <ResultDialog
                open={resultOpen}
                success={resultSuccess}
                errorMessage={resultErrorMessage}
                onClose={() => {
                    setResultOpen(false);
                    if (resultSuccess && createdName) {
                        navigate(`/notification-hub/view/${createdName}`);
                    }
                }}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CreateTemplate;
